/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : actionHandler.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { toast } from "react-toastify";
import customConsole from "../../config/customConsole";
import useAxios from "../../hooks/useAxios";
import { startLoading, stopLoading } from "./spinner";

const baseURL = process.env.REACT_APP_BASE_URL;
export function showToastMessage({ type, message }) {
  switch (type) {
    case "info":
      toast.info(message);
      break;
    case "warn":
      toast.warn(message);
      break;
    case "success":
      toast.success(message);
      break;
    case "error":
      toast.error(message);
      break;
    default:
      toast(message);
  }
}

const buildAxiosConfig = (props) => ({
  ...(props.ignore_app_loading_spinner === true && {
    ignoreAppLoadingSpinner: true,
  }),
});

const resolveUrl = (props) =>
  props.api_name.startsWith("http")
    ? props.api_name
    : `${baseURL}${props.api_name}`;

const createDefaultResp = () => ({
  error: true,
  msg: "",
  data: { error: true, msg: "", data: {} },
});

const performApiCall = async ({ api, props, resolvedUrl, axiosConfig }) => {
  const config = {
    ...axiosConfig,
    ...(props.headers && { headers: props.headers }),
  };

  switch (props.https_method) {
    case "GET":
      if (props.is_file) {
        return api.get(resolvedUrl, {
          params: { filename: props.file_name },
          responseType: "blob",
          ...config,
        });
      }
      if (props.is_get_file) {
        return api.get(resolvedUrl, {
          params: props.params,
          responseType: "blob",
          ...config,
        });
      }
      return api.get(resolvedUrl, { params: props.params, ...config });
    case "DELETE":
      return api.delete(`${baseURL}${props.api_name}`, {
        ...config,
        data: { ...props.params },
      });
    case "PUT":
      return api.put(resolvedUrl, { ...props.params }, config);
    case "POST":
      if (props.is_file) {
        const file = new FormData();
        file.append("name", "filename");
        file.append("filename", props.file);
        return api.post(resolvedUrl, file, config);
      }
      return api.post(resolvedUrl, { ...props.params }, config);
    case "PATCH":
      return api.patch(resolvedUrl, { ...props.params }, config);
    default:
      console.warn("Unhandled HTTP method:", props.https_method);
      return null;
  }
};

const handleResponse = ({ props, resp, dispatch }) => {
  if (!props.action_type) return resp.data;

  if (props.is_file && props.https_method === "GET") {
    const url = URL.createObjectURL(new Blob([resp.data]));
    dispatch({
      type: props.action_type,
      payload: { error: false, msg: "File downloaded", file_path: url },
    });
    return resp.data;
  }

  const adaptedPayload = props.adapter ? props.adapter(resp) : resp.data;
  dispatch({
    type: props.action_type,
    payload: {
      error: resp?.data?.error ?? false,
      msg: resp?.data?.msg ?? "",
      ...adaptedPayload,
    },
  });

  return resp.data;
};

const handleError = ({ error, props }) => {
  customConsole("-- API catch block --");
  customConsole(error);
  if (props.show_toast) {
    const isEntityTooLarge =
      error?.response?.status === 413 ||
      (props.is_file &&
        (error?.message === "File too large to upload" ||
          error?.code === "ERR_NETWORK" ||
          error?.response?.status === 413));

    if (isEntityTooLarge) {
      showToastMessage({
        type: "error",
        message: "Request Entity Too Large. File size exceeds the allowed limit.",
      });
    } else if (error?.response?.data?.msg) {
      showToastMessage({ type: "error", message: error.response.data.msg });
    } else if (error?.response?.data?.message) {
      showToastMessage({ type: "error", message: error.response.data.message });
    } else if (error?.response?.statusText) {
      showToastMessage({ type: "error", message: error.response.statusText });
    } else if (error?.message) {
      showToastMessage({ type: "error", message: error.message });
    }
  }
};
const notifyApiSuccess = ({ props, resp }) => {
  if (props.show_toast && resp.data.msg) {
    showToastMessage({
      type: resp.data.error ? "error" : "success",
      message: resp.data.msg,
    });
  }
  if (typeof props.callback === "function") {
    props.callback(resp.data);
  }
};

const formatApiErrorResponse = ({ error, props, resp }) => {
  const isEntityTooLarge =
    error?.response?.status === 413 ||
    (props.is_file &&
      (error?.message === "Network Error" ||
        error?.code === "ERR_NETWORK" ||
        error?.response?.status === 413));

  const errorMessage = isEntityTooLarge
    ? "Request Entity Too Large. File size exceeds the allowed limit."
    : error?.response?.data?.msg ||
      error?.response?.data?.message ||
      error?.response?.statusText ||
      error?.message ||
      "";

  return {
    ...resp.data,
    error: true,
    status: error?.response?.status || (isEntityTooLarge ? 413 : undefined),
    msg: errorMessage,
  };
};

export function HandleApiActions(props) {
  const api = useAxios();
  customConsole("-- HandleApiActions --");
  customConsole(props);

  return async (dispatch) => {
    const shouldShowSpinner = props.ignore_app_loading_spinner !== true;
    let resp = createDefaultResp();
    try {
      if (shouldShowSpinner) {
        console.log("---loading---");
        dispatch(startLoading());
      }
      let apiActionPerformed = true;
      const axiosConfig = buildAxiosConfig(props);
      console.log("axiosConfig", axiosConfig);
      const resolvedUrl = resolveUrl(props);
      resp = await performApiCall({ api, props, resolvedUrl, axiosConfig });
      if (!resp) {
        apiActionPerformed = false;
        resp = createDefaultResp();
      }

      const data = handleResponse({ props, resp, dispatch });

      if (apiActionPerformed) {
        notifyApiSuccess({ props, resp });
      }
      return data;
    } catch (error) {
      handleError({ error, props });
      return formatApiErrorResponse({ error, props, resp });
    } finally {
      if (shouldShowSpinner) {
        dispatch(stopLoading());
      }
    }
  };

























}
export async function GetMultipleImages(props) {
  const api = useAxios();
  customConsole("-- GetMultipleImages --");
  customConsole(props);
  try {
    const axiosConfig = {
      ...(props.ignore_app_loading_spinner === true && {
        ignoreAppLoadingSpinner: true,
      }),
    };
    return await Promise.all(
      props.map(async (imageDetails) => {
        let resp = { data: "" };
        if (imageDetails.image?.length) {
          resp = await api.get(
            `${baseURL}/UCM/files/details`,
            {
              params: { filename: imageDetails.image },
              responseType: "blob", // Important for handling binary data
            },
            axiosConfig,
          );
        }
        return {
          id: imageDetails.id,
          image: imageDetails.image,
          url: resp?.data
            ? URL.createObjectURL(new Blob([resp.data]))
            : "",
        };
      }),
    );
  } catch (error) {
    customConsole("-- API catch block --");
    customConsole(error);
    if (props.show_toast && error?.response?.data?.msg) {
      showToastMessage({ type: "error", message: error.response.data.msg });
    }
  }
}
export async function GetSingleImages(props) {
  const api = useAxios();
  customConsole("-- GetSingleImages --");
  customConsole(props);
  try {
    const resp = await api.get(`${baseURL}/UCM/files/details`, {
      params: { filename: props.image },
      responseType: "blob", // Important for handling binary data
      ...(props.ignore_app_loading_spinner === true && {
        ignoreAppLoadingSpinner: true,
      }),
    });
    return resp?.data ? URL.createObjectURL(new Blob([resp.data])) : "";
  } catch (error) {
    customConsole("-- API catch block --");
    customConsole(error);
    if (props.show_toast && error?.response?.data?.msg) {
      showToastMessage({ type: "error", message: error.response.data.msg });
    }
  }
}
